import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { createPayfastCheckout } from "@/lib/api/payfast.functions";

/**
  * One-click PayFast checkout button. Calls the server to generate safe
  * PayFast fields, then auto-submits a hidden form to PayFast's process URL.
 * Works for both credit-pack and contract orders (the server fn looks up
 * the order by reference).
 */
export function PayFastCheckoutButton({
  reference,
  label = "Pay securely with PayFast",
}: {
  reference: string;
  label?: string;
}) {
  const create = useServerFn(createPayfastCheckout);
  const [loading, setLoading] = useState(false);

  async function go() {
    setLoading(true);
    try {
      const { processUrl, fields } = (await create({ data: { reference } })) as {
        processUrl: string;
        fields: Record<string, string>;
      };
      // Build + submit a hidden form so PayFast sees a real browser POST.
      const form = document.createElement("form");
      form.method = "POST";
      form.action = processUrl;
      form.style.display = "none";
      for (const [k, v] of Object.entries(fields)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
    } catch (e: any) {
      toast.error("Could not start PayFast checkout", {
        description: e?.message ?? String(e),
      });
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={go}
      disabled={loading}
      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#13B5EA] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:brightness-95 disabled:opacity-70"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Lock className="h-4 w-4" />
      )}
      {loading ? "Redirecting to PayFast…" : label}
    </button>
  );
}
